import classNames from 'classnames';
import AnimatedImage from 'core-lib/components/AnimatedImage';
import ShimmerImage from 'core-lib/components/ShimmerImage';
import { Autoplay, Navigation } from 'core-lib/components/Swiper/Swiper';
import { useComponentInViewport } from 'core-lib/hooks/useComponentInViewport';
import formatCurrency from 'core-lib/utils/currency';
import { formatFuelBalance } from 'core-lib/utils/fuel';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { THEME } from 'src/modules/parkplusStoreV2/constants';
import { Typography } from 'ui-v2/components/Typography';
import useStoreItemClick from '../../../hooks/useStoreItemClick';
import { LayoutPropType } from '../../../types/layout';
import {
    trackProductViewedStoreV2,
    trackSectionViewedStoreV2,
} from '../../../utils/events';
import s from './FloatingScrollListing.module.scss';

const Swiper = dynamic(() => import('core-lib/components/Swiper'), {
  ssr: false,
});

export const FloatingScrollListing = ({ data }: LayoutPropType) => {
  const { handleItemClickWithDelegation } = useStoreItemClick();
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [showShadow, setShowShadow] = useState(false);

  //for click
  const CARDID = data?.layoutType || 'Deals of the day';
  //for event
  const rootRef = useRef<HTMLInputElement>();
  const isEventFired = useRef(false);
  const inViewPort = useComponentInViewport(rootRef, {
    threshold: 1,
  });

  const loyaltyPoints = data?.data?.[activeIndex]?.loyaltyPoints;
  const loyaltyPrice = data?.data?.[activeIndex]?.loyaltyPrice;
  const swiperRef = useRef({});
  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      setLoading(false);
    }, 90);

    return () => clearTimeout(timer); // Cleanup timer on unmount
  }, []);

  useEffect(() => {
    if (inViewPort) {
      if (!isEventFired.current) {
        trackSectionViewedStoreV2(data?.title);
        isEventFired.current = true;
      }
      const product = data?.data?.[activeIndex];
      if (product && !swiperRef.current[product?.productId]) {
        trackProductViewedStoreV2('deals_of_the_day', product?.productId, {
          description: product.eventType,
        });
        swiperRef.current[product?.productId] = true;
      }
    }
  }, [data, inViewPort, activeIndex, data?.title]);

  useEffect(() => {
    if (inViewPort) {
      setShowShadow(true);
    } else {
      setShowShadow(false);
    }
  }, [inViewPort, activeIndex, data?.data]);

  return (
    <div
      className={s.wrapper}
      ref={rootRef}
      style={{
        backgroundImage: `url(${data?.metaData?.headingImage})`,
      }}
      onClick={(e) => {
        handleItemClickWithDelegation(
          e,
          CARDID,
          data?.entityType,
          data?.data,
          'deals_of_the_day'
        );
      }}
    >
      <Swiper
        onSlideChange={(el) => {
          setActiveIndex(el.realIndex);
        }}
        modules={[Navigation, Autoplay]}
        slidesPerView={2}
        loop={true}
        autoplay={{
          delay: 2500,
          disableOnInteraction: false,
        }}
        centeredSlides={true}
        slideToClickedSlide={true}
        spaceBetween={0}
        effect="creative"
        creativeEffect={{
          limitProgress: 2,
          prev: {
            translate: ['-90%', 0, 0],
            scale: 0.4,
          },
          next: {
            translate: ['90%', 0, 0],
            scale: 0.4,
          },
        }}
        slides={data?.data?.map((item, index) => (
          <div
            key={item?.productId}
            className={s.item}
            id={CARDID}
            data-index={index}
          >
            <ShimmerImage
              src={item?.coverImage}
              className={classNames(s.image, {
                [s.animate]:
                  +item?.productId === +data?.data?.[activeIndex]?.productId,
              })}
            />
            {/* {activeIndex > -1 && +item?.productId === +data?.data?.[activeIndex]?.productId && (
              <div className={s.largeShadow}></div>
            )} */}
          </div>
        ))}
      />
      <div className={s.bottom}>
        <Typography
          label={data?.data?.[activeIndex]?.title}
          variant="label_2_semi_bold"
          className={s.title}
          color={data?.textColor === THEME?.DARK ? 'on_dark_bg' : 'primary'}
        />

        {loyaltyPoints > 0 && loyaltyPrice >= 0 && (
          <div className={s.mlPrice}>
            <Typography
              variant="label_3_regular"
              label={formatCurrency(data?.data?.[activeIndex]?.price)}
              className={s.lineThrough}
              color="secondary"
              style={{
                marginRight: '8px',
              }}
            />
            <Typography
              label={`${formatCurrency(loyaltyPrice)} using `}
              variant="label_2_semi_bold"
              color="offer_dark"
            />
            <AnimatedImage
              src="https://parkplus-bkt-img.parkplus.io/production/team/public/FS_20240104160730062768.png"
              className={s.drop}
              alt="mlDrop"
            />
            <Typography
              label={formatFuelBalance(loyaltyPoints)}
              variant="label_2_semi_bold"
              color="offer_dark"
            />
          </div>
        )}
      </div>
    </div>
  );
};
